import {
  Check,
  X,
  Zap,
  Crown,
  Rocket,
  ArrowRight,
  Users,
  Star,
  Sparkles,
  DollarSign,
} from "lucide-react";
import { Link } from "react-router-dom";
import Wrapper from "@/components/global/wrapper";
import Container from "@/components/global/container";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MagicCard } from "@/components/ui/magic-card";
import { OrbitingCircles } from "@/components/ui/orbiting-circles";
import Icons from "@/components/global/icons";
import { motion } from "framer-motion";
import { useState } from "react";

const PricingPage = () => {
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">(
    "monthly",
  );

  const plans = [
    {
      name: "Starter",
      description: "Perfect for small teams getting started with AI marketing",
      icon: Zap,
      prices: { monthly: 29, yearly: 290 },
      savings: 20,
      features: [
        "Up to 5,000 AI-generated content pieces",
        "10 marketing campaigns",
        "Basic analytics dashboard",
        "Email support",
        "Social media integration",
        "Content templates library",
        "1 user account",
      ],

      notIncluded: [
        "Advanced AI insights",
        "Priority support",
        "Custom integrations",
        "White-label options",
      ],

      popular: false,
      cta: "Start Free Trial",
    },
    {
      name: "Professional",
      description:
        "Ideal for growing businesses ready to scale their marketing",
      icon: Crown,
      prices: { monthly: 99, yearly: 990 },
      savings: 20,
      features: [
        "Up to 25,000 AI-generated content pieces",
        "Unlimited marketing campaigns",
        "Advanced analytics & reporting",
        "Priority email & chat support",
        "All social media platforms",
        "A/B testing tools",
        "Up to 5 user accounts",
        "API access",
        "Custom templates",
        "Advanced AI insights",
        "Performance optimization",
      ],

      notIncluded: [
        "White-label options",
        "Dedicated account manager",
        "Custom integrations",
      ],

      popular: true,
      cta: "Start Free Trial",
    },
    {
      name: "Enterprise",
      description: "Comprehensive solution for large organizations",
      icon: Rocket,
      prices: { monthly: 299, yearly: 2990 },
      savings: 20,
      features: [
        "Unlimited AI-generated content",
        "Unlimited campaigns & users",
        "Advanced analytics & reporting",
        "24/7 priority support",
        "All platform integrations",
        "Advanced A/B testing",
        "Custom AI model training",
        "White-label options",
        "Dedicated account manager",
        "Custom integrations",
        "SSO & advanced security",
        "Compliance reports",
        "Advanced API access",
      ],

      notIncluded: [],
      popular: false,
      cta: "Contact Sales",
    },
  ];

  const faqs = [
    {
      question: "Can I switch plans anytime?",
      answer:
        "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate any billing adjustments.",
    },
    {
      question: "What's included in the free trial?",
      answer:
        "Our 14-day free trial gives you full access to the Professional plan features with no credit card required. You can create campaigns, generate content, and explore all our AI-powered tools.",
    },
    {
      question: "How does the AI content generation work?",
      answer:
        "Our AI analyzes your brand voice, target audience, and campaign goals to generate personalized marketing content. Each plan includes different limits on monthly generations.",
    },
    {
      question: "Do you offer refunds?",
      answer:
        "We offer a 30-day money-back guarantee for all paid plans. If you're not satisfied, we'll refund your payment in full.",
    },
    {
      question: "Can I get a custom enterprise solution?",
      answer:
        "Yes! Our Enterprise plan can be customized to meet your specific needs. Contact our sales team to discuss custom pricing, features, and integrations.",
    },
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Marketing Director",
      company: "TechCorp",
      content:
        "Blyrha has transformed our marketing efficiency. We're generating 3x more content in half the time.",
      rating: 5,
    },
    {
      name: "Mike Chen",
      role: "Founder",
      company: "StartupXYZ",
      content:
        "The AI insights have helped us increase our conversion rates by 150%. Best investment we've made.",
      rating: 5,
    },
    {
      name: "Lisa Rodriguez",
      role: "CMO",
      company: "Enterprise Inc",
      content:
        "From small campaigns to enterprise-level automation, Blyrha scales perfectly with our needs.",
      rating: 5,
    },
  ];

  return (
    <Wrapper className="py-20 relative" data-oid="nhgke7n">
      {/* Enhanced Hero Section */}
      <div
        className="relative flex flex-col items-center justify-center w-full py-20"
        data-oid="u2cq7uj"
      >
        {/* Background blur effect */}
        <div
          className="absolute flex lg:hidden size-40 rounded-full bg-blue-500 blur-[10rem] top-0 left-1/2 -translate-x-1/2 -z-10"
          data-oid="rbs0dhm"
        ></div>

        {/* Orbiting circles for desktop */}
        <Container
          className="hidden lg:flex absolute inset-0 top-0 mb-auto flex-col items-center justify-center w-full min-h-[60vh] -z-10"
          data-oid="usx7oqg"
        >
          <OrbitingCircles speed={0.25} radius={220} data-oid="xzrkxid">
            <Icons.circle1
              className="size-3 text-blue-500/60"
              data-oid=":01v-mc"
            />
            <Icons.circle2
              className="size-1 text-purple-500/70"
              data-oid="a8p_s6:"
            />
          </OrbitingCircles>
          <OrbitingCircles speed={0.12} radius={320} data-oid="siglxgh">
            <Icons.circle2
              className="size-1 text-pink-500/50"
              data-oid="unhl9.8"
            />
            <Icons.circle1
              className="size-4 text-blue-500/50"
              data-oid="msz.6em"
            />
            <Icons.circle2
              className="size-1 text-purple-500/80"
              data-oid="4wsajtc"
            />
          </OrbitingCircles>
        </Container>

        <Container data-oid=":m8g:bf">
          <div
            className="text-center max-w-4xl mx-auto mb-16 relative z-10"
            data-oid="2tu-yca"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              data-oid="35dxc8o"
            >
              <div
                className="group relative grid overflow-hidden rounded-full px-4 py-2 shadow-[0_1000px_0_0_hsl(0_0%_15%)_inset] transition-colors duration-200 mx-auto mb-6 w-fit"
                data-oid="iz_nczh"
              >
                <span data-oid="-.0v1af">
                  <span
                    className="spark mask-gradient absolute inset-0 h-[100%] w-[100%] animate-flip overflow-hidden rounded-full [mask:linear-gradient(white,_transparent_50%)] before:absolute before:aspect-square before:w-[200%] before:rotate-[-90deg] before:animate-rotate before:bg-[conic-gradient(from_0deg,transparent_0_340deg,white_360deg)] before:content-[''] before:[inset:0_auto_auto_50%] before:[translate:-50%_-15%]"
                    data-oid="rk9yirn"
                  />
                </span>
                <span
                  className="backdrop absolute inset-[1px] rounded-full bg-background transition-colors duration-200 group-hover:bg-neutral-800"
                  data-oid="p2pkl63"
                />
                <span
                  className="z-10 py-1 text-sm text-neutral-100 flex items-center"
                  data-oid="drvuvnx"
                >
                  <DollarSign
                    className="h-4 w-4 mr-2 text-green-500"
                    data-oid="c2:xzvp"
                  />
                  Transparent Pricing
                </span>
              </div>
            </motion.div>
            <motion.h1
              className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 !leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-oid="on71t5e"
            >
              Simple,{" "}
              <span
                className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent"
                data-oid="jns3-hs"
              >
                Transparent Pricing
              </span>
            </motion.h1>
            <motion.p
              className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              data-oid="w497m5f"
            >
              Choose the perfect plan for your business. Start with our free
              trial and scale as you grow.
            </motion.p>

            {/* Enhanced Billing Toggle */}
            <motion.div
              className="flex w-full justify-center px-4 mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              data-oid="r8e5tev"
            >
              <MagicCard
                gradientFrom="#3b82f6"
                gradientTo="#8b5cf6"
                gradientColor="rgba(59,130,246,0.05)"
                className="bg-background/80 w-full max-w-2xl p-6 rounded-2xl border backdrop-blur-sm"
                data-oid="4gu_4.5"
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between" data-oid="708rvz6">
                  <fieldset className="w-full space-y-3">
                    <legend className="text-xs font-semibold uppercase tracking-[0.3em] text-muted-foreground">
                      Billing period
                    </legend>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setBillingPeriod("monthly")}
                        aria-pressed={billingPeriod === "monthly"}
                        className={`relative flex w-full items-center justify-center rounded-xl px-5 py-3 text-sm sm:text-base font-semibold transition-all duration-200 min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                          billingPeriod === "monthly"
                            ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg border border-transparent"
                            : "bg-transparent text-muted-foreground border border-border/60 hover:text-foreground hover:border-foreground/40"
                        }`}
                      >
                        Monthly
                      </button>
                      <button
                        type="button"
                        onClick={() => setBillingPeriod("yearly")}
                        aria-pressed={billingPeriod === "yearly"}
                        className={`relative flex w-full items-center justify-center rounded-xl px-5 py-3 text-sm sm:text-base font-semibold transition-all duration-200 min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                          billingPeriod === "yearly"
                            ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg border border-transparent"
                            : "bg-transparent text-muted-foreground border border-border/60 hover:text-foreground hover:border-foreground/40"
                        }`}
                      >
                        Yearly
                      </button>
                    </div>
                  </fieldset>

                  <div className="flex w-full flex-col items-center gap-2 sm:w-auto sm:items-end">
                    <Badge
                      variant="secondary"
                      className="text-xs font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-800 border border-emerald-200 px-3 py-1"
                      data-oid="p:85dyh"
                    >
                      Save 20% yearly
                    </Badge>
                    <p className="text-xs text-muted-foreground/80 text-center sm:text-right">
                      Switch plans anytime. Annual billing locks in the best rate.
                    </p>
                  </div>
                </div>
              </MagicCard>
            </motion.div>
          </div>
        </Container>
      </div>

      {/* Pricing Cards */}
      <Container data-oid="yh0umx-">
        <div className="grid lg:grid-cols-3 gap-8 mb-20" data-oid="pasxv::">
          {plans.map((plan, index) => {
            const Icon = plan.icon;
            const price = plan.prices[billingPeriod];
            const savings =
              billingPeriod === "yearly"
                ? Math.round(
                    ((plan.prices.monthly * 12 - plan.prices.yearly) /
                      (plan.prices.monthly * 12)) *
                      100,
                  )
                : 0;

            return (
              <motion.div
                key={plan.name}
                className="relative"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                data-oid="98xdmcq"
              >
                <MagicCard
                  gradientFrom="#3b82f6"
                  gradientTo="#8b5cf6"
                  gradientColor={
                    plan.popular
                      ? "rgba(59,130,246,0.1)"
                      : "rgba(59,130,246,0.05)"
                  }
                  className={`relative p-8 rounded-2xl border-2 bg-card/50 backdrop-blur-sm h-full ${
                    plan.popular
                      ? "border-blue-500 shadow-xl"
                      : "border-border/50"
                  }`}
                  data-oid="x25mbh2"
                >
                  {plan.popular && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.5, delay: 0.3 }}
                      data-oid="fm2bfio"
                    >
                      <Badge
                        className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg"
                        data-oid="4dw_ayu"
                      >
                        <Sparkles className="w-3 h-3 mr-1" data-oid="36w0itw" />
                        Most Popular
                      </Badge>
                    </motion.div>
                  )}

                  <div className="text-center mb-8" data-oid="grkj8oz">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ duration: 0.3 }}
                      data-oid="jqp:msc"
                    >
                      <Icon
                        className="w-12 h-12 text-blue-500 mx-auto mb-4"
                        data-oid="24j7xlc"
                      />
                    </motion.div>
                    <h3 className="text-2xl font-bold mb-2" data-oid="p7:0dfl">
                      {plan.name}
                    </h3>
                    <p
                      className="text-muted-foreground mb-6"
                      data-oid="vta086:"
                    >
                      {plan.description}
                    </p>

                    <div className="space-y-2" data-oid="mbxdzv3">
                      <motion.div
                        className="flex items-baseline justify-center"
                        key={billingPeriod}
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.3 }}
                        data-oid="9z0nxld"
                      >
                        <span className="text-4xl font-bold" data-oid="ksi.q_g">
                          ${price}
                        </span>
                        <span
                          className="text-muted-foreground ml-2"
                          data-oid=":6o6_c9"
                        >
                          /{billingPeriod.slice(0, -2)}
                        </span>
                      </motion.div>
                      {billingPeriod === "yearly" && savings > 0 && (
                        <motion.p
                          className="text-sm text-green-600 font-medium"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3, delay: 0.1 }}
                          data-oid="2wsnih_"
                        >
                          Save {savings}% vs monthly
                        </motion.p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4 mb-8" data-oid="btww1et">
                    {plan.features.map((feature, featureIndex) => (
                      <motion.div
                        key={featureIndex}
                        className="flex items-start"
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{
                          duration: 0.3,
                          delay: featureIndex * 0.05,
                        }}
                        viewport={{ once: true }}
                        data-oid="83_x_qy"
                      >
                        <Check
                          className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0"
                          data-oid="sebfi2e"
                        />
                        <span className="text-sm" data-oid=".pu0wgh">
                          {feature}
                        </span>
                      </motion.div>
                    ))}
                    {plan.notIncluded.map((feature, featureIndex) => (
                      <motion.div
                        key={featureIndex}
                        className="flex items-start opacity-50"
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 0.5, x: 0 }}
                        transition={{
                          duration: 0.3,
                          delay: (plan.features.length + featureIndex) * 0.05,
                        }}
                        viewport={{ once: true }}
                        data-oid="6qv-zms"
                      >
                        <X
                          className="w-5 h-5 text-muted-foreground mr-3 mt-0.5 flex-shrink-0"
                          data-oid="5h71nnm"
                        />
                        <span
                          className="text-sm text-muted-foreground"
                          data-oid="yfyi4qe"
                        >
                          {feature}
                        </span>
                      </motion.div>
                    ))}
                  </div>

                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-oid="s.2ks.u"
                  >
                    {plan.cta === "Contact Sales" ? (
                      <Button
                        className={`w-full ${
                          plan.popular
                            ? "bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 shadow-lg shadow-blue-500/25"
                            : "border-border/50 hover:bg-background/80"
                        }`}
                        variant={plan.popular ? "default" : "outline"}
                        size="lg"
                        data-oid="p521os1"
                      >
                        {plan.cta}
                      </Button>
                    ) : (
                      <Link to="/demo" data-oid="p521os1">
                        <Button
                          className={`w-full ${
                            plan.popular
                              ? "bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 shadow-lg shadow-blue-500/25"
                              : "border-border/50 hover:bg-background/80"
                          }`}
                          variant={plan.popular ? "default" : "outline"}
                          size="lg"
                        >
                          {plan.cta}
                          <ArrowRight className="w-4 h-4 ml-2" data-oid="12o6qkn" />
                        </Button>
                      </Link>
                    )}
                  </motion.div>
                </MagicCard>
              </motion.div>
            );
          })}
        </div>
      </Container>

      {/* Enhanced Social Proof */}
      <Container data-oid="jesrh0b">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          data-oid="6np5t3:"
        >
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-12"
            data-oid=".b9904p"
          >
            Trusted by{" "}
            <span
              className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
              data-oid="eia3hgn"
            >
              thousands
            </span>{" "}
            of marketers
          </h2>
          <div className="grid md:grid-cols-3 gap-8" data-oid="92r1i_0">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                data-oid="76y:8cl"
              >
                <MagicCard
                  gradientFrom="#3b82f6"
                  gradientTo="#8b5cf6"
                  gradientColor="rgba(59,130,246,0.05)"
                  className="p-6 rounded-xl border bg-card/50 backdrop-blur-sm h-full"
                  data-oid="piu1hoa"
                >
                  <div className="flex items-center mb-4" data-oid="z8jxqts">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        transition={{ duration: 0.2, delay: i * 0.1 }}
                        viewport={{ once: true }}
                        data-oid="_jsv.:0"
                      >
                        <Star
                          className="w-5 h-5 text-yellow-400 fill-current"
                          data-oid="k5hwma:"
                        />
                      </motion.div>
                    ))}
                  </div>
                  <p
                    className="text-muted-foreground mb-4 italic"
                    data-oid="6hud8mv"
                  >
                    "{testimonial.content}"
                  </p>
                  <div data-oid="bz_gyeg">
                    <p className="font-semibold" data-oid="lb82_f3">
                      {testimonial.name}
                    </p>
                    <p
                      className="text-sm text-muted-foreground"
                      data-oid=".h1rj77"
                    >
                      {testimonial.role} at {testimonial.company}
                    </p>
                  </div>
                </MagicCard>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </Container>

      {/* Enhanced FAQ Section */}
      <Container data-oid="qm-z4vy">
        <motion.div
          className="max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          data-oid="lvd13zm"
        >
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-12"
            data-oid="kpfvm.k"
          >
            Frequently Asked{" "}
            <span
              className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
              data-oid="5js3phd"
            >
              Questions
            </span>
          </h2>
          <div className="space-y-6" data-oid="2j_ej0s">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                data-oid="hdq8uw3"
              >
                <MagicCard
                  gradientFrom="#3b82f6"
                  gradientTo="#8b5cf6"
                  gradientColor="rgba(59,130,246,0.05)"
                  className="p-6 rounded-xl border bg-card/50 backdrop-blur-sm"
                  data-oid="m5nqzl2"
                >
                  <h3 className="text-lg font-semibold mb-3" data-oid="jiel9t_">
                    {faq.question}
                  </h3>
                  <p className="text-muted-foreground" data-oid="0-hz488">
                    {faq.answer}
                  </p>
                </MagicCard>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </Container>

      {/* Enhanced CTA Section */}
      <Container data-oid="h.w5dow">
        <motion.div
          className="relative text-center p-12 rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 text-white mt-20 overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          data-oid="wwse5vd"
        >
          {/* Animated background elements */}
          <motion.div
            className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.1, 0.3, 0.1],
            }}
            transition={{ duration: 4, repeat: Infinity }}
            data-oid="u-et449"
          />

          <motion.div
            className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full blur-xl"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.1, 0.2, 0.1],
            }}
            transition={{ duration: 3, repeat: Infinity, delay: 1 }}
            data-oid="xwq1cnq"
          />

          <div className="relative z-10" data-oid="jarse76">
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              data-oid=".l4onmx"
            >
              <Rocket
                className="h-12 w-12 mx-auto mb-4 text-white/80"
                data-oid="py-piyb"
              />
            </motion.div>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4"
              data-oid="rrrys34"
            >
              Ready to Transform Your Marketing?
            </h2>
            <p
              className="text-xl mb-8 opacity-90 max-w-2xl mx-auto"
              data-oid="-2az.5n"
            >
              Start your free trial today. No credit card required. Cancel
              anytime.
            </p>
            <div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              data-oid="agcy5j_"
            >
              <Link to="/demo" data-oid="5ns1489">
                <Button
                  size="lg"
                  variant="secondary"
                  className="px-8 bg-white text-blue-600 hover:bg-white/90"
                >
                  Start Free Trial
                </Button>
              </Link>
              <Link to="/contact" data-oid="6puzio8">
                <Button
                  size="lg"
                  variant="outline"
                  className="px-8 border-white text-white hover:bg-white/10 backdrop-blur-sm"
                  data-oid="y66yj3x"
                >
                  Contact Sales
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </Container>
    </Wrapper>
  );
};

export default PricingPage;
